"use client";

import { useAtom } from "jotai";
import type { Interval } from "@/lib/api";
import {
  CANDLE_PRESETS,
  candleColorsAtom,
  chartTypeAtom,
  intervalAtom,
  linkBuySellToChartAtom,
  type ChartType,
} from "@/lib/chart-prefs";

const INTERVALS: Interval[] = ["1m", "5m", "15m", "1h", "4h", "1d"];
const CHART_TYPES: { id: ChartType; label: string }[] = [
  { id: "candles", label: "Candles" },
  { id: "bars", label: "Bars" },
  { id: "line", label: "Line" },
  { id: "area", label: "Area" },
];

const segment =
  "px-2.5 py-1 text-xs rounded transition-colors border border-transparent";
const active = "bg-raised text-fg border-line";
const idle = "text-fg-muted hover:text-fg";

const ChartToolbar = () => {
  const [interval, setInterval] = useAtom(intervalAtom);
  const [chartType, setChartType] = useAtom(chartTypeAtom);
  const [colors, setColors] = useAtom(candleColorsAtom);
  const [linked, setLinked] = useAtom(linkBuySellToChartAtom);

  const activePreset =
    Object.entries(CANDLE_PRESETS).find(
      ([, c]) => c.up === colors.up && c.down === colors.down
    )?.[0] ?? "custom";

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex items-center gap-0.5" role="group" aria-label="Interval">
        {INTERVALS.map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => setInterval(i)}
            aria-pressed={interval === i}
            className={`${segment} tnum ${interval === i ? active : idle}`}
            // This market prints ~5 trades/hour, so sub-hour buckets are mostly empty.
            // Say so rather than letting the user think the chart is broken.
            title={
              i === "1m" || i === "5m"
                ? "This market trades ~5x/hour. Short intervals will look sparse"
                : undefined
            }
          >
            {i}
          </button>
        ))}
      </div>

      <div className="h-4 w-px bg-line" aria-hidden="true" />

      <div className="flex items-center gap-0.5" role="group" aria-label="Chart type">
        {CHART_TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setChartType(t.id)}
            aria-pressed={chartType === t.id}
            className={`${segment} ${chartType === t.id ? active : idle}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(chartType === "candles" || chartType === "bars") && (
        <>
          <div className="h-4 w-px bg-line" aria-hidden="true" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-fg-muted">Colors</span>
            {Object.entries(CANDLE_PRESETS).map(([name, preset]) => (
              <button
                key={name}
                type="button"
                onClick={() => setColors(preset)}
                aria-pressed={activePreset === name}
                title={
                  name === "colorblind-safe"
                    ? "Blue/orange, distinguishable with red-green color blindness"
                    : name
                }
                className={`flex h-6 items-center gap-1 rounded border px-1.5 transition-colors ${
                  activePreset === name ? "border-line bg-raised" : "border-transparent hover:bg-raised"
                }`}
              >
                <span
                  className="h-3 w-1.5 rounded-xs"
                  style={{ backgroundColor: preset.up }}
                  aria-hidden="true"
                />
                <span
                  className="h-3 w-1.5 rounded-xs"
                  style={{ backgroundColor: preset.down }}
                  aria-hidden="true"
                />
              </button>
            ))}

            {/* Without this the colourblind-safe preset gives a blue/orange chart beside a
                red/green order book, two encodings disagreeing, which is worse than either
                alone. On by default; off for users who want the book fixed to red/green. */}
            <label className="flex cursor-pointer select-none items-center gap-1.5 pl-1 text-xs text-fg-muted">
              <input
                type="checkbox"
                checked={linked}
                onChange={(e) => setLinked(e.target.checked)}
                className="h-3 w-3 accent-brand"
              />
              Apply to buy/sell
            </label>
          </div>
        </>
      )}
    </div>
  );
};

export default ChartToolbar;
